import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  Loader2,
  Mail,
  Search,
  Sparkles,
  UserRound,
  Users,
  X,
} from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { getProjectbyID } from "reducer/projectsSlice";
import { API } from "service/Interceptor";
import { updateUserStatus } from "service/service";
import { applicantDisplayName, debounce, serverFiles } from "utils/utils";
import {
  applicantIdentityKeys,
  getCreatorBriefApplicants,
  getCreatorBriefs,
  isApplyComment,
  mapPostToOpenBrief,
  parseApplyComment,
  setCreatorBriefApplicantStatus,
} from "utils/creatorBriefs";
import toast from "components/Toast/toast";
import ApplicantDetailsModal from "components/app/ApplicantDetailsModal";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const TABS = [
  { key: "", label: "All" },
  { key: "SHORTLIST", label: "Shortlisted" },
  { key: "SELECTED", label: "Selected" },
  { key: "REJECTED", label: "Rejected" },
];

const STATUS_STYLE = {
  PENDING: "status-pill status-pill-pending",
  SHORTLIST: "status-pill status-pill-shortlisted",
  SELECTED: "status-pill status-pill-selected",
  REJECTED: "status-pill status-pill-rejected",
};

const PER_PAGE = 10;

const initials = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar = ({ src, name }) =>
  src ? (
    <img
      src={src}
      alt={name}
      className="h-12 w-12 rounded-full object-cover shrink-0"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = NO_IMAGE;
      }}
    />
  ) : (
    <div className="avatar-fallback h-12 w-12 rounded-full shrink-0 flex items-center justify-center font-semibold text-sm">
      {initials(name)}
    </div>
  );

const ConfirmModal = ({ open, title, body, confirmLabel, tone, onConfirm, onClose, busy }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          className="relative w-full max-w-md rounded-2xl border applicants-modal p-6"
        >
          <h3 className="font-display font-bold text-xl applicants-title tracking-tight">
            {title}
          </h3>
          <p className="mt-2 text-sm applicants-muted leading-relaxed">{body}</p>
          <div className="mt-6 flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="applicants-modal-cancel rounded-full border px-4 py-2 text-sm font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className={`rounded-full px-4 py-2 text-sm font-semibold text-white transition-colors inline-flex items-center gap-2 disabled:opacity-50 ${
                tone === "danger"
                  ? "bg-rose-600 hover:bg-rose-500"
                  : "bg-[#6C63F5] hover:bg-[#5751E5]"
              }`}
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {confirmLabel}
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const silentGet = (url, config = {}) =>
  API.get(url, { ...config, skipErrorToast: true });

const ApplicantsPage = () => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const { project } = useSelector((state) => state.projects);
  const { userProfile } = useSelector((state) => state.userProfile);

  const urlIsPostBrief = String(projectId || "").startsWith("post-");
  const urlPostId = urlIsPostBrief
    ? String(projectId).replace(/^post-/, "")
    : null;

  const extractJobUuid = (text) => {
    const m = String(text || "").match(
      /jobId:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i
    );
    return m?.[1] || null;
  };

  const [resolvedPostId, setResolvedPostId] = useState(urlPostId);
  const [resolvedJobId, setResolvedJobId] = useState(
    UUID_RE.test(String(projectId || "")) ? String(projectId) : null
  );
  const [jobExists, setJobExists] = useState(false);
  const [sourceReady, setSourceReady] = useState(false);

  const isCreatorBrief = urlIsPostBrief || (!!resolvedPostId && !jobExists);
  const creatorPostId = resolvedPostId || urlPostId;

  const [applicants, setApplicants] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [detailsApp, setDetailsApp] = useState(null);
  const [creatorBriefTitle, setCreatorBriefTitle] = useState("");

  useEffect(() => {
    let cancelled = false;
    setSourceReady(false);
    setJobExists(false);
    setResolvedPostId(urlPostId);
    setResolvedJobId(
      UUID_RE.test(String(projectId || "")) ? String(projectId) : null
    );

    (async () => {
      let postId = urlPostId;
      let jobUuid = UUID_RE.test(String(projectId || ""))
        ? String(projectId)
        : null;

      if (urlPostId) {
        try {
          const res = await silentGet(`api/post/${urlPostId}`);
          const post = res?.data?.data || res?.data;
          if (post?.id) postId = post.id;
          jobUuid =
            extractJobUuid(post?.content) ||
            extractJobUuid(post?.description) ||
            jobUuid;
        } catch {
          /* keep ids */
        }
      }

      const creatorKey =
        userProfile?.userAliasId || userProfile?.id || userProfile?.userId;
      const local = getCreatorBriefs(creatorKey).find(
        (b) =>
          String(b?.jobId) === String(projectId) ||
          (urlPostId && String(b?.postId) === String(urlPostId))
      );
      if (!postId) {
        postId =
          local?.postId ||
          (String(local?.jobId || "").startsWith("post-")
            ? String(local.jobId).replace(/^post-/, "")
            : null);
      }
      if (local?.jobId && UUID_RE.test(String(local.jobId))) {
        jobUuid = jobUuid || String(local.jobId);
      }

      const matchPost = (p) => {
        const brief = mapPostToOpenBrief(p);
        const content = String(p?.content || "");
        const needle = String(projectId || "");
        return (
          (brief && String(brief.jobId) === needle) ||
          (urlPostId && String(p?.id) === String(urlPostId)) ||
          (needle && content.includes(`jobId: ${needle}`)) ||
          (needle && UUID_RE.test(needle) && content.includes(needle))
        );
      };

      const searchBriefs = async (extra = {}) => {
        const res = await silentGet("api/post", {
          params: {
            pageNo: 1,
            perPage: 80,
            sortBy: "latest",
            tags: "BRIEFS",
            ...extra,
          },
        });
        const results = res?.data?.data?.results || [];
        const match = results.find(matchPost);
        if (match && !jobUuid) {
          jobUuid = extractJobUuid(match.content);
        }
        return match?.id || null;
      };

      if (!postId) {
        try {
          if (userProfile?.userAliasId) {
            postId = await searchBriefs({
              postOwnerId: userProfile.userAliasId,
            });
          }
          if (!postId) postId = await searchBriefs();
        } catch {
          /* keep postId */
        }
      }

      let exists = false;
      if (jobUuid) {
        try {
          const res = await silentGet(`api/jobs/${jobUuid}`);
          exists = !!(res?.data?.success && res?.data?.data);
        } catch {
          exists = false;
        }
      }

      if (cancelled) return;
      setJobExists(exists);
      setResolvedPostId(postId || null);
      setResolvedJobId(jobUuid || null);
      setSourceReady(true);
      if (exists && jobUuid) {
        dispatch(getProjectbyID({ id: jobUuid, skipErrorToast: true }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, projectId, urlIsPostBrief, urlPostId, userProfile?.userAliasId, userProfile?.id, userProfile?.userId]);

  useEffect(() => {
    if (!isCreatorBrief) return;
    const creatorKey =
      userProfile?.userAliasId || userProfile?.id || userProfile?.userId;
    const local = getCreatorBriefs(creatorKey).find(
      (b) =>
        String(b?.jobId) === String(projectId) ||
        (creatorPostId && String(b?.postId) === String(creatorPostId))
    );
    if (local?.jobTitle) {
      setCreatorBriefTitle(local.jobTitle);
      return;
    }
    if (!creatorPostId) {
      setCreatorBriefTitle("Your brief");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await silentGet(`api/post/${creatorPostId}`);
        const post = res?.data?.data || res?.data;
        const title =
          post?.title ||
          String(post?.content || "")
            .split("\n")
            .map((l) => l.trim())
            .find(Boolean) ||
          "Your brief";
        if (!cancelled) setCreatorBriefTitle(title);
      } catch {
        if (!cancelled) setCreatorBriefTitle("Your brief");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCreatorBrief, projectId, creatorPostId, userProfile]);

  const normalizeAppStatus = (value) => {
    const raw = String(value ?? "PENDING").toUpperCase();
    if (raw === "SELECT" || raw === "2") return "SELECTED";
    if (raw === "1" || raw === "SHORTLISTED") return "SHORTLIST";
    if (raw === "3") return "REJECTED";
    if (raw === "0" || raw === "PENDING") return "PENDING";
    return raw;
  };

  const fetchApplicants = useCallback(
    async (reset = false) => {
      if (reset) setLoading(true);
      try {
        const localApps = [
          ...getCreatorBriefApplicants(projectId),
          ...(creatorPostId
            ? getCreatorBriefApplicants(`post-${creatorPostId}`)
            : []),
        ];
        const statusByUser = new Map();
        localApps.forEach((a) => {
          const key = String(a.userId || a.id || "");
          if (key) {
            statusByUser.set(
              key,
              normalizeAppStatus(a.applicationStatus || a.status)
            );
          }
        });

        const mapComment = (c) => {
          const owner = c?.postOwner || c?.commentOwner || c?.user || {};
          const name =
            `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim() ||
            owner?.name ||
            "Applicant";
          const userAliasId = owner?.userAliasId || null;
          const userId = owner?.userId || owner?.id || userAliasId || c?.userId;
          const parsed = parseApplyComment(c?.comment || c?.content || "");
          const savedStatus = statusByUser.get(String(userId));
          const industryRaw = owner?.industry;
          const industry = Array.isArray(industryRaw)
            ? industryRaw.filter((x) => typeof x === "string")
            : typeof industryRaw === "string"
              ? [industryRaw]
              : [];
          return {
            id: c?.id || c?.commentId,
            applicationNumber: c?.id || c?.commentId || userId,
            userId,
            userAliasId,
            name,
            firstName: owner?.firstName,
            lastName: owner?.lastName,
            profileUrl: owner?.profileUrl,
            email: owner?.email || null,
            mobileNo: owner?.mobileNo || null,
            message: c?.comment || c?.content || "Applied to your brief",
            note: parsed.note,
            workLinks: parsed.workLinks,
            portfolioUrl: parsed.portfolioUrl,
            applicationStatus: savedStatus || "PENDING",
            appliedAt: c?.createdAt,
            industry,
          };
        };

        let fromComments = [];
        if (creatorPostId) {
          const res = await silentGet(`api/post/${creatorPostId}/comment`);
          const comments = res?.data?.data?.results || res?.data?.data || [];
          const list = Array.isArray(comments) ? comments : [];
          fromComments = list
            .filter((c) => isApplyComment(c?.comment || c?.content || ""))
            .map(mapComment);
        }

        let fromJobs = [];
        const jobKey =
          resolvedJobId ||
          (UUID_RE.test(String(projectId || "")) ? String(projectId) : null);
        if (jobKey) {
          try {
            const res = await silentGet(`api/jobs/${jobKey}/applicants`, {
              params: {
                page: 0,
                size: 100,
                searchTerm: search || undefined,
              },
            });
            fromJobs = (res?.data?.data?.results || []).map((a) => ({
              ...a,
              applicationStatus: normalizeAppStatus(
                a?.applicationStatus || "PENDING"
              ),
              industry: Array.isArray(a?.industry)
                ? a.industry.filter((x) => typeof x === "string")
                : typeof a?.industry === "string"
                  ? [a.industry]
                  : [],
            }));
          } catch {
            fromJobs = [];
          }
        }

        const seen = new Map();
        let merged = [];
        for (const a of [...fromJobs, ...fromComments, ...localApps]) {
          const row = {
            ...a,
            applicationStatus: normalizeAppStatus(
              a.applicationStatus || a.status || "PENDING"
            ),
            applicationNumber: a.applicationNumber || a.id || a.userId,
          };
          const keys = applicantIdentityKeys(row);
          const hit = keys.find((k) => seen.has(k));
          if (hit) {
            const idx = seen.get(hit);
            merged[idx] = {
              ...row,
              ...merged[idx],
              note: merged[idx].note || row.note,
              workLinks: merged[idx].workLinks?.length
                ? merged[idx].workLinks
                : row.workLinks,
              email: merged[idx].email || row.email,
              userId: merged[idx].userId || row.userId,
              userAliasId: merged[idx].userAliasId || row.userAliasId,
            };
            continue;
          }
          const idx = merged.length;
          merged.push(row);
          keys.forEach((k) => seen.set(k, idx));
          const fallback = String(row.userId || row.id || row.applicationNumber || "");
          if (fallback && !keys.length) seen.set(`f:${fallback}`, idx);
        }

        if (status) {
          merged = merged.filter(
            (a) => normalizeAppStatus(a.applicationStatus) === status
          );
        }
        if (search) {
          const q = search.toLowerCase();
          merged = merged.filter((a) => {
            const name = applicantDisplayName(a);
            return (
              name.toLowerCase().includes(q) ||
              String(a?.email || "")
                .toLowerCase()
                .includes(q) ||
              String(a?.note || a?.message || "")
                .toLowerCase()
                .includes(q)
            );
          });
        }

        setApplicants(merged);
        setHasMore(false);
      } catch (err) {
        console.error(err);
        setApplicants(getCreatorBriefApplicants(projectId));
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [projectId, creatorPostId, resolvedJobId, status, search]
  );

  useEffect(() => {
    if (!sourceReady) return;
    setPage(1);
    setHasMore(false);
    fetchApplicants(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, status, search, isCreatorBrief, sourceReady, creatorPostId, resolvedJobId]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
      }, 400),
    []
  );

  const runStatusUpdate = async () => {
    if (!confirm) return;
    setBusyId(confirm.userId);
    try {
      let saved = false;
      if (UUID_RE.test(String(projectId || "")) && jobExists) {
        try {
          await updateUserStatus(confirm.status, projectId, confirm.userId);
          saved = true;
        } catch {
          saved = false;
        }
      }
      if (!saved) {
        setCreatorBriefApplicantStatus(
          projectId,
          confirm.userId,
          confirm.status
        );
        if (creatorPostId) {
          setCreatorBriefApplicantStatus(
            `post-${creatorPostId}`,
            confirm.userId,
            confirm.status
          );
        }
      }
      const okMsg =
        confirm.status === "SHORTLIST"
          ? "Candidate shortlisted"
          : confirm.status === "SELECTED"
            ? "Candidate selected"
            : "Candidate rejected";
      toast.success(okMsg);
      setApplicants((prev) =>
        prev.map((a) =>
          a.userId === confirm.userId
            ? { ...a, applicationStatus: confirm.status }
            : a
        )
      );
      setDetailsApp((prev) =>
        prev?.userId === confirm.userId && prev?.preloaded
          ? {
              ...prev,
              preloaded: {
                ...prev.preloaded,
                applicationStatus: confirm.status,
                status: confirm.status,
              },
            }
          : prev
      );
      setConfirm(null);
    } catch (_) {
      const errMsg =
        confirm.status === "SHORTLIST"
          ? "Failed to shortlist candidate"
          : confirm.status === "SELECTED"
            ? "Failed to select candidate"
            : "Failed to reject candidate";
      toast.error(errMsg);
    } finally {
      setBusyId(null);
    }
  };

  const briefTitle = isCreatorBrief
    ? creatorBriefTitle || "Your brief"
    : project?.jobTitle || "Loading brief…";

  const title =
    status === "SHORTLIST"
      ? "Shortlisted applicants"
      : status === "SELECTED"
        ? "Selected applicants"
        : status === "REJECTED"
          ? "Rejected applicants"
          : "Applicants";

  return (
    <div className="applicants-page mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
      <Link
        to="/projects"
        className="applicants-back inline-flex items-center gap-1.5 text-sm transition-colors mb-6"
      >
        <ArrowLeft size={15} /> Back to projects
      </Link>

      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#8b83ff] font-medium mb-2">
          Your brief
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter applicants-title">
          {title}
        </h1>
        <p className="mt-2 applicants-subtitle max-w-2xl">
          {briefTitle}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="flex flex-wrap gap-2 flex-1">
          {TABS.map((tab) => (
            <button
              key={tab.label}
              type="button"
              onClick={() => setStatus(tab.key)}
              className={`applicants-filter-chip inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                status === tab.key ? "is-active" : ""
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
          />
          <input
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value.trim());
            }}
            placeholder="Search applicants…"
            className="applicants-search w-full rounded-full border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#6C63F5]"
          />
        </div>
      </div>

      {loading && applicants.length === 0 ? (
        <div className="py-20 text-center applicants-muted flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading applicants…
        </div>
      ) : applicants.length === 0 ? (
        <div className="applicant-empty rounded-2xl border border-dashed py-16 text-center">
          <Users size={22} className="mx-auto mb-3 applicants-muted" />
          <p className="applicants-empty-title font-medium">No applicants yet</p>
          <p className="mt-1 text-sm applicants-muted">
            When creatives apply to this brief, they&apos;ll show up here.
          </p>
        </div>
      ) : (
        <InfiniteScroll
          dataLength={applicants.length}
          next={() => fetchApplicants(false)}
          hasMore={hasMore}
          loader={
            <div className="py-6 text-center text-zinc-500 flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading more…
            </div>
          }
        >
          <div className="space-y-3">
            {applicants.map((a, i) => {
              const name = applicantDisplayName(a);
              const avatarSrc = a?.profileUrl ? serverFiles(a.profileUrl) : null;
              const statusKey = normalizeAppStatus(a?.applicationStatus);
              const isRejected = statusKey === "REJECTED";
              const isSelected = statusKey === "SELECTED";
              const isShortlisted = statusKey === "SHORTLIST";
              const industry = Array.isArray(a?.industry)
                ? a.industry.filter((x) => typeof x === "string").join(", ")
                : typeof a?.industry === "string"
                  ? a.industry
                  : null;
              const statusLabel =
                statusKey === "SHORTLIST"
                  ? "shortlisted"
                  : statusKey === "SELECTED"
                    ? "selected"
                    : statusKey.toLowerCase();

              return (
                <motion.div
                  key={a?.userId || a?.applicationNumber || i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (i % 8) * 0.03 }}
                  className="applicant-tile rounded-2xl border p-4 sm:p-5 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <Avatar src={avatarSrc} name={name} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="applicant-tile-name font-display font-semibold truncate">
                            {name}
                          </p>
                          <span className={STATUS_STYLE[statusKey] || STATUS_STYLE.PENDING}>
                            {statusLabel}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs applicant-tile-meta">
                          {a?.email && (
                            <span className="inline-flex items-center gap-1 truncate">
                              <Mail size={12} /> {a.email}
                            </span>
                          )}
                          {industry && (
                            <span className="inline-flex items-center gap-1 truncate">
                              <UserRound size={12} /> {industry}
                            </span>
                          )}
                          {a?.appliedAt && (
                            <span>
                              Applied{" "}
                              {new Date(a.appliedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 sm:justify-end shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          setDetailsApp({
                            // Brand JobApplication UUID → user-details (has email).
                            // Comment numeric id → legacy creator post path.
                            applicationNumber: a?.applicationNumber,
                            name,
                            userId: a?.userId,
                            seed: a,
                            preloaded: isCreatorBrief ? a : null,
                          })
                        }
                        className="brief-owner-btn brief-owner-btn-edit rounded-full px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5"
                      >
                        View details
                      </button>
                      <button
                        type="button"
                        disabled={
                          isShortlisted ||
                          isSelected ||
                          isRejected ||
                          busyId === a?.userId
                        }
                        onClick={() =>
                          setConfirm({
                            status: "SHORTLIST",
                            userId: a.userId,
                            name,
                          })
                        }
                        className="applicant-action applicant-action-shortlist rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                      >
                        <Check size={13} />
                        {isShortlisted ? "Shortlisted" : "Shortlist"}
                      </button>
                      <button
                        type="button"
                        disabled={
                          isSelected || isRejected || busyId === a?.userId
                        }
                        onClick={() =>
                          setConfirm({
                            status: "SELECTED",
                            userId: a.userId,
                            name,
                          })
                        }
                        className="applicant-action applicant-action-select rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                      >
                        <Sparkles size={13} />
                        {isSelected ? "Selected" : "Select"}
                      </button>
                      <button
                        type="button"
                        disabled={isRejected || busyId === a?.userId}
                        onClick={() =>
                          setConfirm({
                            status: "REJECTED",
                            userId: a.userId,
                            name,
                          })
                        }
                        className="applicant-action applicant-action-reject rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                      >
                        <X size={13} />
                        {isRejected ? "Rejected" : "Reject"}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </InfiniteScroll>
      )}

      <ConfirmModal
        open={!!confirm}
        title={
          confirm?.status === "SHORTLIST"
            ? "Shortlist this applicant?"
            : confirm?.status === "SELECTED"
              ? "Select this applicant?"
              : "Reject this applicant?"
        }
        body={
          confirm?.status === "SHORTLIST"
            ? `Move ${confirm?.name || "this applicant"} to your shortlist.`
            : confirm?.status === "SELECTED"
              ? `Mark ${confirm?.name || "this applicant"} as selected for this brief.`
              : `Reject ${confirm?.name || "this applicant"} for this brief.`
        }
        confirmLabel={
          confirm?.status === "SHORTLIST"
            ? "Shortlist"
            : confirm?.status === "SELECTED"
              ? "Select"
              : "Reject"
        }
        tone={confirm?.status === "REJECTED" ? "danger" : "primary"}
        busy={!!busyId}
        onConfirm={runStatusUpdate}
        onClose={() => !busyId && setConfirm(null)}
      />

      <ApplicantDetailsModal
        open={!!detailsApp}
        applicationNumber={detailsApp?.applicationNumber}
        projectId={projectId}
        postId={isCreatorBrief ? creatorPostId : null}
        briefTitle={briefTitle}
        seed={detailsApp?.seed || null}
        preloaded={detailsApp?.preloaded || null}
        actionBusy={!!busyId}
        onClose={() => setDetailsApp(null)}
        onShortlist={(d) => {
          const name = applicantDisplayName(d || detailsApp);
          setConfirm({
            status: "SHORTLIST",
            userId: d?.userId || detailsApp?.userId,
            name,
          });
        }}
        onSelect={(d) => {
          const name = applicantDisplayName(d || detailsApp);
          setConfirm({
            status: "SELECTED",
            userId: d?.userId || detailsApp?.userId,
            name,
          });
        }}
        onReject={(d) => {
          const name =
            applicantDisplayName(d || detailsApp) ||
            "Applicant";
          setConfirm({
            status: "REJECTED",
            userId: d?.userId || detailsApp?.userId,
            name,
          });
        }}
      />
    </div>
  );
};

export default ApplicantsPage;
